import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import * as Constants from "../api/constants";
import AdminMetaDataForm from "../components/AdminMetaDataForm";
import AdminLayout from "../components/AdminLayout";
import AddonItemImage from "../components/AddonItemImage";
import { Context } from "../App";
import { useNavigate } from "react-router-dom";
import "../adminConsoleTheme.css";

/**
 * SUPER_ADMIN add-on catalogue — mirrors Book now add-ons layout; theatres/timeslots live under Update theatre details.
 */
const ChangeMetadata = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [data, setData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isNewForm, setIsNewForm] = useState(false);
  const [selectedItemData, setSelectedItemData] = useState({});
  const { context } = useContext(Context);
  const navigate = useNavigate();

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (String(context.adminRole || "").toUpperCase() !== "SUPER_ADMIN") {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, context.adminRole, navigate]);

  const refreshMeta = useCallback(() => {
    axios
      .get(Constants.GET_META_DATA_ADDONS)
      .then((response) => setData(response.data))
      .catch((error) => console.error("Error fetching add-on metadata", error));
  }, []);

  useEffect(() => {
    refreshMeta();
  }, [refreshMeta]);

  const addonCategories = useMemo(() => {
    const raw = data?.addonsData || {};
    return Object.keys(raw).filter((k) => Array.isArray(raw[k]));
  }, [data]);

  useEffect(() => {
    if (addonCategories.length && !selectedCategory) {
      setSelectedCategory(addonCategories[0]);
    }
  }, [addonCategories, selectedCategory]);

  const currentAddonList = data?.addonsData?.[selectedCategory] || [];

  const openNewAddon = () => {
    setSelectedItemData({});
    setIsNewForm(true);
    setShowPopup(true);
  };

  const editItem = (row) => {
    setSelectedItemData(row || {});
    setIsNewForm(false);
    setShowPopup(true);
  };

  const togglePopup = (isOpen) => {
    setShowPopup(isOpen);
  };

  return (
    <AdminLayout title="Addons">
      <section className="admin-addons-catalogue text-white pb-6">
        <div className="admin-console-card mb-3">
          <div className="admin-console-card-title">Add-on catalogue</div>
          <p className="text-secondary small mb-0">
            Same category layout as <strong>Book now → add-ons</strong>. Click a card to edit. Image uploads use the
            shared site-media API path (
            <code className="text-info">/media/site-page/…</code>).
          </p>
        </div>

        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-1 lg:flex-row">
          <aside
            className="w-full shrink-0 rounded-2xl border border-white/20 p-3 lg:w-56"
            style={{ background: "rgba(10, 6, 20, 0.95)" }}
          >
            <p className="mb-2 px-2 text-xs font-bold uppercase tracking-wider text-[#c9c1d9]">Categories</p>
            <nav className="flex flex-row flex-wrap gap-1.5 lg:flex-col">
              {addonCategories.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedCategory(key)}
                  className={`rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-all duration-200 lg:w-full ${
                    key === selectedCategory
                      ? "border border-transparent bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-lg ring-1 ring-white/20"
                      : "border border-white/10 bg-white/5 text-white/80 hover:border-violet-400/30 hover:bg-white/10"
                  }`}
                >
                  {key}
                </button>
              ))}
            </nav>
            <Button variant="outline-light" size="sm" className="mt-3 w-100" onClick={openNewAddon}>
              Add new add-on
            </Button>
          </aside>

          <main
            className="min-w-0 flex-1 rounded-2xl border border-slate-200/80 bg-white p-4 text-[#06132b] md:p-6"
            style={{ boxShadow: "0 12px 48px -16px rgba(22,0,90,0.15)" }}
          >
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2 border-b border-slate-200 pb-2">
              <h2 className="text-xl font-black text-[#16005a]">{selectedCategory || "—"}</h2>
              <Button variant="primary" size="sm" onClick={openNewAddon}>
                Add in this category
              </Button>
            </div>
            {!currentAddonList.length ? (
              <p className="text-slate-500">No items in this category.</p>
            ) : (
              <div className="mb-6 flex flex-wrap justify-center gap-4 sm:gap-5 md:gap-6">
                {currentAddonList.map((propsList) => (
                  <div
                    key={propsList.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => editItem(propsList)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") editItem(propsList);
                    }}
                    className="addon-list group flex w-[10.5rem] cursor-pointer flex-col items-stretch rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/95 p-3 text-center shadow-[0_6px_24px_-8px_rgba(22,0,90,0.12)] transition duration-200 hover:-translate-y-1 hover:border-violet-300/70 sm:w-[11.5rem] sm:p-4 md:w-[12rem]"
                  >
                    <div className="mb-2 flex h-40 w-full items-center justify-center rounded-xl bg-slate-50/90 p-3 ring-1 ring-slate-200/80 sm:h-44 md:h-48">
                      <AddonItemImage
                        imageName={propsList.imageName}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <span className="logo-color-1 mb-2 min-h-[2.5rem] px-0.5 text-sm font-semibold leading-snug text-[#16005a]">
                      {propsList.itemName}
                    </span>
                    <span className="mt-auto rounded-lg bg-[#16005a]/[0.06] py-1.5 text-base font-black text-[#16005a]">
                      <i className="bi bi-currency-rupee text-[0.85em]" aria-hidden />
                      {propsList.price}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>

        <Modal
          show={showPopup}
          className="admin-console-modal"
          contentClassName="border-0 shadow-lg"
          onHide={() => togglePopup(false)}
          centered
          scrollable
          size="lg"
        >
          <AdminMetaDataForm
            type={isNewForm ? "Add Addon" : selectedCategory}
            selectedTab="Addons"
            data={selectedItemData}
            togglePopup={togglePopup}
            categoryTypeDefault={selectedCategory}
            onSaved={refreshMeta}
          />
        </Modal>
      </section>
    </AdminLayout>
  );
};

export default ChangeMetadata;
